/**
 * Color utility functions for dynamic theming and contrast handling.
 */

/**
 * Calculate perceived brightness using HSP algorithm.
 * @returns brightness value 0-255
 */
export function getPerceivedBrightness(color: string): number {
  let r: number;
  let g: number;
  let b: number;

  if (color.match(/^rgb/)) {
    const matches = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);
    if (matches === null) {
      return 255;
    }
    r = parseInt(matches[1], 10);
    g = parseInt(matches[2], 10);
    b = parseInt(matches[3], 10);
  } else {
    let hex = color.replace(/^#/, "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    const num = parseInt(hex, 16);
    r = (num >> 16) & 255;
    g = (num >> 8) & 255;
    b = num & 255;
  }

  return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
}

/**
 * Determine if a color is light or dark based on perceived brightness.
 */
export function isColorLight(color: string): boolean {
  const brightness = getPerceivedBrightness(color);
  return brightness > 127.5;
}

/**
 * Get the appropriate contrast text color for a given background.
 * Uses threshold of 180 for better readability.
 */
export function getContrastColor(hexBackground: string): string {
  const brightness = getPerceivedBrightness(hexBackground);
  return brightness > 180 ? "#1F2937" : "#FFFFFF";
}

/**
 * Get text color classes for light/dark backgrounds.
 * Returns appropriate tailwind classes for text visibility.
 */
export function getTextColorClasses(backgroundColor: string): {
  text: string;
  textMuted: string;
  textLink: string;
} {
  const isLight = isColorLight(backgroundColor);

  if (isLight) {
    return {
      text: "text-gray-900",
      textMuted: "text-gray-600",
      textLink: "text-gray-700 hover:text-gray-900",
    };
  }

  return {
    text: "text-white",
    textMuted: "text-white/70",
    textLink: "text-white/80 hover:text-white",
  };
}
